import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, SkipForward, SkipBack, Music, X, ChevronUp, ChevronDown } from 'lucide-react';
import { usePlaylistMusic } from '../../context/PlaylistMusicContext';
import { useTheme } from '../../context/ThemeContext';
import { useMusicKit } from '../../context/MusicKitContext';
import { useAuth } from '../../context/AuthContext';
import MusicSearchModal from '../sessions/MusicSearchModal';
import LyricsViewer from './LyricsViewer';
import { prefetchLyrics } from '../../services/LyricsPrefetcher';
import FireEmoji from './FireEmoji';

// Import fingerprint SVG
import FingerprintIcon from '../../assets/fingerprint-icon.svg';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlaylistSoundBar - "FULL SONG BAR" design
 * Used for: Playlists and Sessions
 * Features: Queue toggle, Fire emoji, Fingerprint (tap-in), Play controls
 * Reference name: "Full Song Bar" or "Queue Bar"
 */
const PlaylistSoundBar = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [fireAnimation, setFireAnimation] = useState(false);
  const [showMusicSearch, setShowMusicSearch] = useState(false);
  const [firedSongIds, setFiredSongIds] = useState(new Set());
  const [showQueue, setShowQueue] = useState(false); // Queue toggle state
  const [showLyrics, setShowLyrics] = useState(false); // Lyrics viewer
  
  // Persist minimized state
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('tapInMiniPlayerMinimized') === 'true';
    } catch {
      return false;
    }
  });
  
  useEffect(() => {
    try {
      localStorage.setItem('tapInMiniPlayerMinimized', isMinimized.toString());
    } catch {}
  }, [isMinimized]);
  
  const {
    currentPlaylistId,
    currentPlaylist,
    currentTrack,
    isPlaylistPlaying,
    playlistQueue,
    currentTrackIndex,
    togglePlayPause,
    skipNext,
    skipPrevious,
    hasPlaylist,
    playSongAtIndex,
    canAddSongs,
    addSongToPlaylist,
    fireCount = 0,
    sendFireReaction,
  } = usePlaylistMusic();

  const {
    isPlaying: musicKitIsPlaying,
    currentSong: musicKitCurrentSong,
    play: musicKitPlay,
    pause: musicKitPause,
    playbackTime,
    duration,
    seekTo
  } = useMusicKit();

  const isPlaying = musicKitIsPlaying;

  // Check if user is the playlist owner/host
  const isHost = useMemo(() => {
    if (!currentPlaylist || !user) return false;
    return currentPlaylist.owner_id === user.id || currentPlaylist.created_by === user.id;
  }, [currentPlaylist, user]);

  // Check if tap-in is allowed (host setting)
  const isTapInAllowed = useMemo(() => {
    if (!currentPlaylist) return false;
    return currentPlaylist.allow_tap_in !== false; // Default to true if not set
  }, [currentPlaylist]);

  // Can current user use fingerprint (tap in)?
  const canUseTapIn = isHost || isTapInAllowed;

  // Check if user has already fired this song
  const currentSongId = currentTrack?.id || musicKitCurrentSong?.id;
  const hasUserFiredCurrentSong = currentSongId ? firedSongIds.has(currentSongId) : false;

  // Display song info
  const displaySong = useMemo(() => {
    if (musicKitCurrentSong) {
      let artworkUrl = musicKitCurrentSong.artworkUrl || '';
      if (artworkUrl && artworkUrl.includes('{w}')) {
        artworkUrl = artworkUrl.replace('{w}', '300').replace('{h}', '300');
      }
      return {
        title: musicKitCurrentSong.name,
        artist: musicKitCurrentSong.artistName,
        artwork_url: artworkUrl,
        id: musicKitCurrentSong.id
      };
    }
    if (currentTrack) {
      let artworkUrl = currentTrack.artwork_url || currentTrack.artworkUrl || '';
      if (artworkUrl && artworkUrl.includes('{w}')) {
        artworkUrl = artworkUrl.replace('{w}', '300').replace('{h}', '300');
      }
      return { ...currentTrack, artwork_url: artworkUrl };
    }
    return null;
  }, [musicKitCurrentSong, currentTrack]);

  // Prefetch lyrics when song changes
  useEffect(() => {
    if (displaySong?.title && displaySong?.artist) {
      prefetchLyrics(displaySong.title, displaySong.artist);
    }
  }, [displaySong?.title, displaySong?.artist]);

  // Progress bar
  const progressPercent = duration > 0 ? (playbackTime / duration) * 100 : 0;
  
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Handlers
  const handleTogglePlayPause = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    console.log('=== PAUSE BUTTON CLICKED ===');
    console.log('[PlaylistSoundBar] handleTogglePlayPause called');
    console.log('[PlaylistSoundBar] isPlaying:', isPlaying);
    console.log('[PlaylistSoundBar] musicKitCurrentSong:', musicKitCurrentSong?.id);
    console.log('[PlaylistSoundBar] playlistQueue.length:', playlistQueue.length);
    
    // If we have a playlist with songs but nothing is playing, start from first song
    if (!isPlaying && !musicKitCurrentSong && playlistQueue.length > 0) {
      console.log('[PlaylistSoundBar] Starting playlist from first song');
      if (playSongAtIndex) {
        playSongAtIndex(0);
      }
      return;
    }
    
    // If we have a current track but it's not loaded in MusicKit, load and play it
    if (!isPlaying && currentTrack && !musicKitCurrentSong) {
      console.log('[PlaylistSoundBar] Loading current track:', currentTrack);
      if (playSongAtIndex) {
        playSongAtIndex(currentTrackIndex);
      }
      return;
    }
    
    // Normal play/pause toggle
    if (isPlaying) {
      console.log('[PlaylistSoundBar] Attempting to PAUSE');
      
      // Call MusicKit pause
      if (musicKitPause) {
        console.log('[PlaylistSoundBar] Calling musicKitPause()');
        musicKitPause();
      }
      
      // FALLBACK: Also try to pause any HTML5 audio elements directly
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach((audio, index) => {
        console.log(`[PlaylistSoundBar] Pausing audio element ${index}`);
        audio.pause();
      });
    } else {
      console.log('[PlaylistSoundBar] Attempting to PLAY');
      if (musicKitPlay) {
        musicKitPlay();
      }
    }
  }, [isPlaying, musicKitPlay, musicKitPause, musicKitCurrentSong, playlistQueue, playSongAtIndex, currentTrack, currentTrackIndex]);

  const handleSkipNext = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    skipNext();
  }, [skipNext]);

  const handleSkipPrevious = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    skipPrevious();
  }, [skipPrevious]);

  const handleSeek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (seekTo && duration > 0) {
      seekTo(newTime);
    }
  }, [seekTo, duration]);

  // Fire emoji handler - only 1x per song per user
  const handleFireReaction = useCallback(async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (!currentSongId || hasUserFiredCurrentSong) return;
    
    // Animate
    setFireAnimation(true);
    setTimeout(() => setFireAnimation(false), 500);
    
    // Mark as fired locally
    setFiredSongIds(prev => new Set([...prev, currentSongId]));
    
    // Send to server if available
    if (sendFireReaction) {
      try {
        await sendFireReaction(currentSongId);
      } catch (err) {
        console.error('[PlaylistSoundBar] Fire reaction error:', err);
      }
    }
  }, [currentSongId, hasUserFiredCurrentSong, sendFireReaction]);

  // Fingerprint (Tap In) handler
  const handleTapIn = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (!canUseTapIn) return;
    setShowMusicSearch(true);
  }, [canUseTapIn]);

  const handleAddSong = useCallback(async (song) => {
    if (addSongToPlaylist) {
      await addSongToPlaylist(song);
    }
    setShowMusicSearch(false);
  }, [addSongToPlaylist]);

  const handleNavigateToPlaylist = useCallback(() => {
    if (currentPlaylistId) {
      navigate(`/app/playlist/${currentPlaylistId}`);
    }
  }, [navigate, currentPlaylistId]);

  // Only show when music is actually playing or has been loaded
  if (!displaySong && !isPlaying) return null;

  // Minimized view - only show if we have a song
  if (isMinimized) {
    if (!displaySong?.artwork_url) return null;
    return (
      <div className="fixed bottom-6 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="w-11 h-11 rounded-full overflow-hidden transition-all hover:scale-110 active:scale-95 relative"
          style={{
            boxShadow: isDarkMode 
              ? '0 0 15px rgba(168, 85, 247, 0.5), 0 0 30px rgba(168, 85, 247, 0.2)' 
              : '0 0 15px rgba(168, 85, 247, 0.4)',
          }}
          data-testid="playlist-mini-player-minimized"
        >
          <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
          {isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="flex gap-0.5">
                <div className="w-0.5 h-2.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="playlist-sound-bar">
        <div className="max-w-[430px] mx-auto px-3">
          <div 
            className={`mb-3 rounded-2xl overflow-visible relative`}
            style={{
              background: isDarkMode ? 'rgba(55, 55, 60, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              boxShadow: isDarkMode 
                ? '0 0 20px rgba(168, 85, 247, 0.3), 0 -2px 15px rgba(0,0,0,0.3)' 
                : '0 -4px 20px rgba(0,0,0,0.1)',
            }}
          >
            {/* Queue Toggle Bar - Tap to expand/collapse */}
            <button
              onClick={() => setShowQueue(!showQueue)}
              className={`w-full px-2 py-0.5 flex items-center justify-center gap-1 border-b transition-all ${
                isDarkMode 
                  ? 'border-gray-700/50 hover:bg-white/5' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="playlist-queue-toggle"
            >
              <span className={`text-[12px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Up Next
              </span>
              <span className={`text-[9px] px-1 rounded ${
                isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {playlistQueue.length}
              </span>
              {showQueue ? (
                <ChevronDown className={`w-2.5 h-2.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <ChevronUp className={`w-2.5 h-2.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </button>

            {/* Expandable Queue List */}
            {showQueue && (
              <div className={`max-h-[180px] overflow-y-auto ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                {playlistQueue.length === 0 ? (
                  <div className="py-3 text-center">
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No songs in queue</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {playlistQueue.map((song, index) => {
                      const isCurrentSong = index === currentTrackIndex;
                      return (
                        <div
                          key={song.id || index}
                          onClick={() => playSongAtIndex && playSongAtIndex(index)}
                          className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${
                            isCurrentSong
                              ? isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                              : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                          }`}
                        >
                          <span className={`w-4 text-center text-xs ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            {isCurrentSong ? '▶' : index + 1}
                          </span>
                          <div className="w-7 h-7 rounded overflow-hidden flex-shrink-0 bg-gray-600">
                            {song.artwork_url ? (
                              <img src={song.artwork_url.replace('{w}', '100').replace('{h}', '100')} alt={song.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Music className="w-3 h-3 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {song.title || song.name}
                            </p>
                            <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {song.artist || song.artistName}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* X button - small circle top left */}
            <button 
              onClick={() => setIsMinimized(true)}
              className={`absolute -top-2 -left-1 w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 active:bg-gray-500' 
                  : 'bg-gray-300 hover:bg-gray-400 text-gray-600 active:bg-gray-500'
              }`}
              style={{ touchAction: 'manipulation' }}
              data-testid="playlist-player-close"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
            
            <div className="px-2 py-2 flex items-center gap-1.5">
              {/* Fire Emoji - LEFT side - New Gradient Design */}
              <FireEmoji
                size="md"
                count={fireCount || 0}
                isActive={hasUserFiredCurrentSong}
                isAnimating={fireAnimation}
                onClick={handleFireReaction}
                disabled={hasUserFiredCurrentSong}
                isDarkMode={isDarkMode}
                className="ml-3 flex-shrink-0"
              />

              {/* Album art */}
              <button 
                onClick={handleNavigateToPlaylist}
                className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 hover:scale-105 transition-transform"
              >
                {displaySong?.artwork_url ? (
                  <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-600 rounded-lg flex items-center justify-center">
                    <Music className="w-4 h-4 text-gray-400" />
                  </div>
                )}
              </button>
              
              {/* Song info */}
              <div className="flex-1 min-w-0">
                <button onClick={handleNavigateToPlaylist} className="text-left hover:opacity-80 transition-opacity w-full">
                  <p className={`font-medium truncate text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {displaySong?.title || 'No song playing'}
                  </p>
                  <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {displaySong?.artist || ''}
                  </p>
                </button>
                {displaySong?.title && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowLyrics(true); }}
                    className="text-[10px] text-purple-400 hover:text-purple-300 underline block"
                    style={{ touchAction: 'manipulation' }}
                    data-testid="playlist-view-lyrics"
                  >
                    View Lyrics
                  </button>
                )}
              </div>
              
              {/* Previous */}
              <button 
                onClick={handleSkipPrevious}
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 active:scale-90 transition-all ${
                  isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                }`}
                style={{ touchAction: 'manipulation', minWidth: '32px', minHeight: '32px' }}
                disabled={currentTrackIndex === 0}
                data-testid="playlist-player-prev"
              >
                <SkipBack className={`w-3 h-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} ${currentTrackIndex === 0 ? 'opacity-30' : ''}`} fill="currentColor" />
              </button>
              
              {/* Play/Pause */}
              <button 
                onClick={handleTogglePlayPause}
                className="w-10 h-10 aspect-square rounded-full flex items-center justify-center flex-shrink-0 active:scale-90 transition-all bg-[#6366f1] text-white"
                style={{ touchAction: 'manipulation', minWidth: '40px', minHeight: '40px' }}
                data-testid="playlist-player-play-pause"
              >
                {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
              </button>
              
              {/* Next */}
              <button 
                onClick={handleSkipNext}
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 active:scale-90 transition-all ${
                  isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                }`}
                style={{ touchAction: 'manipulation', minWidth: '32px', minHeight: '32px' }}
                disabled={currentTrackIndex >= playlistQueue.length - 1}
                data-testid="playlist-player-next"
              >
                <SkipForward className={`w-3 h-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} ${currentTrackIndex >= playlistQueue.length - 1 ? 'opacity-30' : ''}`} fill="currentColor" />
              </button>

              {/* Fingerprint - RIGHT side (Host only, if allowed) */}
              <button 
                onClick={handleTapIn}
                disabled={!canUseTapIn}
                className={`w-8 h-8 rounded-full overflow-hidden flex-shrink-0 transition-all ${
                  canUseTapIn 
                    ? 'hover:scale-110 active:scale-95' 
                    : 'opacity-30 cursor-not-allowed grayscale'
                }`}
                style={{ touchAction: 'manipulation' }}
                data-testid="playlist-player-tapin"
                title={!canUseTapIn ? 'Tap In not allowed' : 'Add a song'}
              >
                <img 
                  src={FingerprintIcon} 
                  alt="Tap In"
                  className="w-full h-full object-contain"
                />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="px-2 pb-1.5">
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(playbackTime)}
                </span>
                <div 
                  className={`flex-1 h-1 rounded-full cursor-pointer ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                  onClick={handleSeek}
                  data-testid="playlist-progress-bar"
                >
                  <div 
                    className={`h-full rounded-full transition-all duration-100 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lyrics Viewer */}
      {showLyrics && displaySong?.title && (
        <LyricsViewer
          songTitle={displaySong.title}
          artistName={displaySong.artist || ''}
          artworkUrl={displaySong.artwork_url || ''}
          currentPlaybackTime={playbackTime}
          duration={duration}
          isPlaying={isPlaying}
          onSeek={(time) => { if (seekTo) seekTo(time); }}
          onClose={() => setShowLyrics(false)}
        />
      )}

      {/* Music Search Modal */}
      {showMusicSearch && (
        <MusicSearchModal 
          isOpen={showMusicSearch}
          onClose={() => setShowMusicSearch(false)}
          onAddSong={handleAddSong}
          context="playlist"
          isHost={true}
        />
      )}
    </>
  );
};

export default PlaylistSoundBar;
